import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";

import {
  Client,
  FileDownloadResponse,
  LiquidationAggregation,
  LiquidationSettlementType,
} from "types";
import { ROUTES, SETTLEMENT_TYPES, STATUS_CODES } from "constants/index";
import { readLiquidationDetails } from "api/liquidations";

import { arsFormatter, formatDateOnlyDMY } from "helpers";
import { MdDelete, MdDownload, MdSearch } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ENTITY } from "constants/entities";
import { useEffect, useRef, useState } from "react";
import useModals from "hooks/useModals";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { exportLiquidationToExcel } from "helpers/exportToExcel";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { LIQUIDATIONS_URL } from "constants/urls";
import DetailConfirmationModal from "components/utils/DetailConfirmationModal";
import { buildConsolidatedLiquidation, formatLiquidationNo } from "helpers/liquidations";

type Props = {
  loading: boolean;
  liquidations: LiquidationAggregation[];
  onDelete: (id: number) => void;
  client: {
    id: number;
    label: string;
  } | null;
  clients: Client[];
};

function checkSettlementType(
  liquidation_settlement_type: LiquidationSettlementType,
) {
  switch (liquidation_settlement_type.settlement_type) {
    case SETTLEMENT_TYPES.ABONO:
      return `${arsFormatter(liquidation_settlement_type.calculation)}`;
    case SETTLEMENT_TYPES.PALLETS:
      return `${arsFormatter(
        liquidation_settlement_type.calculation,
      )} (x Pallet)`;
    case SETTLEMENT_TYPES.UNIDADES:
      return `${arsFormatter(liquidation_settlement_type.calculation)} (x Unidad)`;
    case SETTLEMENT_TYPES.PESO:
      return `${arsFormatter(liquidation_settlement_type.calculation)} (x Kg)`;
    case SETTLEMENT_TYPES.PESO_VOLUMETRICO:
      return `${arsFormatter(liquidation_settlement_type.calculation)} (x Kg)`;
    case SETTLEMENT_TYPES.VOLUMEN:
      return `${arsFormatter(liquidation_settlement_type.calculation)} (x M3)`;
    case SETTLEMENT_TYPES.SUPERFICIE:
      return `${arsFormatter(liquidation_settlement_type.calculation)} (x M2)`;
    default:
      return "-";
  }
}

const CustomTableCell = styled(TableCell)<TableCellProps>(({ theme }) => ({
  fontSize: 12,
}));

export default function LiquidationsHistoryTable({
  loading,
  liquidations,
  onDelete,
  client,
  clients,
}: Props) {
  const navigate = useNavigate();
  const { showSnackBar } = useToast();
  const exportAttemptRef = useRef<symbol | null>(null);

  const [liquidationToDelete, setLiquidationToDelete] = useState<
    number | undefined
  >(undefined);

  const [liquidationToExport, setLiquidationToExport] = useState<
    number | undefined
  >(undefined);

  const [liquidationToConfirm, setLiquidationToConfirm] = useState<
    number | undefined
  >(undefined);

  const { deleteModalOpen, setDeleteModalOpen } = useModals();

  const [confirmExportOpen, setConfirmExportOpen] = useState<boolean>(false);
  const { fetchData: fetchLiquidation } = useCrud<FileDownloadResponse>(LIQUIDATIONS_URL, {
    disableAutoFetch: true,
    authAutoFetch: true,
  });

  const exportLiquidation = (id: number) => {
    if (exportAttemptRef.current) return;
    setLiquidationToConfirm(id);
    setConfirmExportOpen(true);
  };

  useEffect(() => () => { exportAttemptRef.current = null; }, []);

  const handleConfirmExport = async (hasDetail: boolean) => {
    if (exportAttemptRef.current) return;
    const id = liquidationToConfirm;
    if (!id || !Number.isSafeInteger(id) || id <= 0) {
      showSnackBar("Seleccione una liquidacion valida para exportar.", "error");
      return;
    }

    const attempt = Symbol();
    exportAttemptRef.current = attempt;
    setLiquidationToExport(id);

    try {
      const response = await fetchLiquidation(String(id), true);
      if (exportAttemptRef.current !== attempt) return;
      if (response.status !== STATUS_CODES.OK) {
        const message = response.data?.message;
        throw new Error(typeof message === "string" && message.trim()
          ? message : "No se pudo obtener la liquidacion para exportar.");
      }

      const detail = await readLiquidationDetails(response.data, id);
      if (exportAttemptRef.current === attempt) {
        exportLiquidationToExcel(detail[0], hasDetail);
      }
    } catch (error) {
      if (exportAttemptRef.current === attempt) {
        showSnackBar(error instanceof Error ? error.message : "No se pudo exportar la liquidacion.", "error");
      }
    } finally {
      if (exportAttemptRef.current === attempt) {
        exportAttemptRef.current = null;
        setLiquidationToExport(undefined);
        setLiquidationToConfirm(undefined);
      }
    }
  };

  return (
    <TableContainer component={Paper} elevation={4}>
      <Box width="100%" p={2}>
        <Typography variant="h6">Historial de Liquidaciones</Typography>
      </Box>

      {loading ? (
        <Box width="100%" p={2}>
          <LinearProgress />
        </Box>
      ) : (
        <Table aria-label="Tabla de liquidaciones" size="small">
          <TableHead>
            <TableRow>
              <CustomTableCell>Nº</CustomTableCell>
              {!client && <CustomTableCell>Cliente</CustomTableCell>}
              <CustomTableCell align="right">Desde</CustomTableCell>
              <CustomTableCell align="right">Hasta</CustomTableCell>
              <CustomTableCell align="right">Total Recepción</CustomTableCell>
              <CustomTableCell align="right">Total Estadía</CustomTableCell>
              <CustomTableCell align="right">Total Despacho</CustomTableCell>
              <CustomTableCell align="right">Total Adicionales</CustomTableCell>
              <CustomTableCell align="right">Total General</CustomTableCell>
              <CustomTableCell align="right" />
            </TableRow>
          </TableHead>

          <TableBody>
            {liquidations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13}>
                  <Alert
                    severity="info"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      No hay registros para el cliente seleccionado
                    </Typography>
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              liquidations.map((lq) => {
                const consolidatedLiquidation = buildConsolidatedLiquidation(lq);

                const incomeSettlementType =
                  consolidatedLiquidation.liquidation_settlement_types?.[0];
                const staySettlementType =
                  consolidatedLiquidation.liquidation_settlement_types?.[1];
                const dischargeSettlementType =
                  consolidatedLiquidation.liquidation_settlement_types?.[2];

                const liqClient = clients.find((c) => c.id === lq.client_id);

                return (
                  <TableRow key={lq.id}>
                    <CustomTableCell>{formatLiquidationNo(lq)}</CustomTableCell>

                    {!client && (
                      <CustomTableCell>
                        {liqClient?.code} - {liqClient?.razon_social}
                      </CustomTableCell>
                    )}

                    <CustomTableCell align="right">
                      {formatDateOnlyDMY(lq.date_from)}
                    </CustomTableCell>

                    <CustomTableCell align="right">
                      {formatDateOnlyDMY(lq.date_to)}
                    </CustomTableCell>

                    <CustomTableCell align="right">
                      {incomeSettlementType
                        ? arsFormatter(incomeSettlementType.subtotal)
                        : "-"}
                    </CustomTableCell>

                    <CustomTableCell align="right">
                      {staySettlementType
                        ? arsFormatter(staySettlementType.subtotal)
                        : "-"}
                    </CustomTableCell>

                    <CustomTableCell align="right">
                      {dischargeSettlementType
                        ? arsFormatter(dischargeSettlementType.subtotal)
                        : "-"}
                    </CustomTableCell>

                    <CustomTableCell align="right">
                      {arsFormatter(consolidatedLiquidation.subtotal_adicionales)}
                    </CustomTableCell>

                    <CustomTableCell align="right">
                      {incomeSettlementType &&
                      staySettlementType &&
                      dischargeSettlementType
                        ? arsFormatter(consolidatedLiquidation.total)
                        : "-"}
                    </CustomTableCell>

                    <CustomTableCell align="right" sx={{ px: 1, py: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Tooltip title="Ver liquidación" arrow>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`${ROUTES.SALES}/${lq.id}`)}
                          >
                            <MdSearch />
                          </IconButton>
                        </Tooltip>

                        {liquidationToExport !== undefined && lq.id === liquidationToExport ? (
                          <CircularProgress size="0.7rem" />
                        ) : (
                          <Tooltip title="Exportar liquidación" arrow>
                            <IconButton
                              size="small"
                              disabled={liquidationToExport !== undefined}
                              onClick={() => exportLiquidation(lq.id || 0)}
                            >
                              <MdDownload />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Eliminar liquidación" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setLiquidationToDelete(lq.id);
                              setDeleteModalOpen(true);
                            }}
                          >
                            <MdDelete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CustomTableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      )}

      <DeleteConfirmationModaL
        open={deleteModalOpen}
        handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
        entityName={ENTITY.LIQUIDATIONS}
        values={[liquidationToDelete?.toString() || ""]}
        handleDelete={() => onDelete(liquidationToDelete || 0)}
      />

      <DetailConfirmationModal
        open={confirmExportOpen}
        handleOpen={() => setConfirmExportOpen(!confirmExportOpen)}
        handleConfirm={(detail) => void handleConfirmExport(detail)}
      />
    </TableContainer>
  );
}
